/**
 * "Birine öner" for the television.
 *
 * A separate component from the web's `RecommendButton` rather than a shared one with a
 * branch inside. The difference is not styling: every target here has to be a registered
 * focusable so the remote can reach it, the panel has to be dismissable with Back, and
 * there is no text input — a note typed on a remote costs more than it is worth, so the TV
 * sends the film and nothing else.
 *
 * The profile list is fetched when the panel opens, not with the page. On a panel, one
 * request at the moment of use is better than one on every detail screen the household
 * browses past.
 */
import { useState } from 'react';
import type { ProfileDto } from '@filmodasi/shared';
import { api } from '../../api/client';
import { useSession } from '../../store/session';
import { useFocusable } from './focus/useFocusable';

function Target({
  profile,
  sent,
  onSelect,
}: {
  profile: ProfileDto;
  sent: boolean;
  onSelect: () => void;
}) {
  const { ref, focused } = useFocusable<HTMLButtonElement>({
    id: `recommend:${profile.id}`,
    group: 'detail:recommend',
    onSelect,
  });
  return (
    <button
      ref={ref}
      type="button"
      className={`tvrecommend__target${focused ? ' is-focused' : ''}`}
      onClick={onSelect}
    >
      <span className="tvrecommend__initial" style={{ background: profile.avatarColor }}>
        {[...profile.name][0]?.toLocaleUpperCase('tr') ?? '?'}
      </span>
      <span>{profile.name}</span>
      {sent && <span className="tvrecommend__done">gönderildi</span>}
    </button>
  );
}

function OpenAction({ onSelect }: { onSelect: () => void }) {
  const { ref, focused } = useFocusable<HTMLButtonElement>({
    id: 'detail:recommend',
    group: 'detail:actions',
    onSelect,
  });
  return (
    <button
      ref={ref}
      type="button"
      className={`tvaction${focused ? ' is-focused' : ''}`}
      onClick={onSelect}
    >
      Birine öner
    </button>
  );
}

export function TvRecommend({ itemId }: { itemId: string }) {
  const { profile: current } = useSession();
  const [open, setOpen] = useState(false);
  const [profiles, setProfiles] = useState<ProfileDto[] | null>(null);
  const [sentTo, setSentTo] = useState<string[]>([]);
  const [failure, setFailure] = useState<string | null>(null);

  async function openPanel() {
    setOpen(true);
    setFailure(null);
    if (profiles) return;
    try {
      const all = await api<ProfileDto[]>('/api/profiles');
      // Recommending to yourself is refused by the server, so it is not offered.
      setProfiles(all.filter((p) => p.id !== current?.id));
    } catch (err) {
      setFailure(err instanceof Error ? err.message : 'Profiller yüklenemedi');
    }
  }

  async function send(receiverProfileId: string) {
    setFailure(null);
    try {
      await api('/api/profile-recommendations', {
        method: 'POST',
        body: { itemId, receiverProfileId, message: null },
      });
      setSentTo((prev) => [...prev, receiverProfileId]);
    } catch (err) {
      setFailure(err instanceof Error ? err.message : 'Gönderilemedi');
    }
  }

  if (!open) return <OpenAction onSelect={() => void openPanel()} />;

  return (
    <div className="tvrecommend">
      <p className="tvrecommend__head">Kime önerilsin?</p>
      {failure && <p className="error">{failure}</p>}
      {profiles === null && <p className="tvrecommend__hint">Yükleniyor…</p>}
      {profiles?.length === 0 && (
        <p className="tvrecommend__hint">Önerebileceğiniz başka bir profil yok.</p>
      )}
      <div className="tvrecommend__targets">
        {profiles?.map((p) => (
          <Target
            key={p.id}
            profile={p}
            sent={sentTo.includes(p.id)}
            onSelect={() => void send(p.id)}
          />
        ))}
        {/*
          An explicit way out, registered in the same group so the remote reaches it by
          moving right past the last profile. Back also closes the panel — this is for the
          viewer who is looking at the screen rather than remembering the button.
        */}
        <CloseTarget onSelect={() => setOpen(false)} />
      </div>
    </div>
  );
}

function CloseTarget({ onSelect }: { onSelect: () => void }) {
  const { ref, focused } = useFocusable<HTMLButtonElement>({
    id: 'recommend:close',
    group: 'detail:recommend',
    onSelect,
  });
  return (
    <button
      ref={ref}
      type="button"
      className={`tvrecommend__target tvrecommend__target--close${focused ? ' is-focused' : ''}`}
      onClick={onSelect}
    >
      Vazgeç
    </button>
  );
}
